import {
    addDoc,
    arrayRemove,
    arrayUnion,
    collection,
    collectionGroup,
    type CollectionReference,
    deleteDoc,
    deleteField as firestoreDeleteField,
    doc,
    type DocumentData,
    endAt,
    type FieldValue,
    getCountFromServer,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    type QueryConstraint,
    serverTimestamp,
    setDoc,
    startAfter,
    startAt,
    updateDoc,
    where,
    writeBatch,
} from 'firebase/firestore';
import { whenAppCheckReady } from '../firebase/appCheck';
import { getFirebaseFirestore } from '../firebase/config';
import { createLogger } from '../utils/logger';
import {
    type DocumentWithId,
    type IModelClass,
    modelFromDoc,
    modelsFromDocs,
} from './modelsFromDocs';

const logger = createLogger('FirestoreRepository');

// The global 'db' instance is removed to ensure lazy initialization.

/**
 * Utility type for Firestore writes — replaces Date fields with Date | FieldValue
 * so services can pass serverTimestamp() without casting to any.
 *
 * Mirrors the server-side type in packages/core/src/interfaces/IFirestoreRepository.ts
 * but uses FieldValue from the client SDK (firebase/firestore).
 */
export type FirestoreWritable<T> = {
    [K in keyof T]: T[K] extends Date
        ? Date | FieldValue
        : T[K] extends Date | undefined
        ? Date | FieldValue | undefined
        : T[K] extends object
        ? FirestoreWritable<T[K]>
        : T[K];
};

/**
 * Partial version of FirestoreWritable for update operations.
 */
export type PartialFirestoreWritable<T> = Partial<FirestoreWritable<T>>;

/**
 * `DocumentWithId` (a model plus its Firestore document ID) and `IModelClass`
 * (a model class with `fromJSON`) live beside the conversion itself, which has
 * no Firestore SDK dependency so a server-seeded page can use it too.
 */
export type { DocumentWithId, IModelClass, RawFirestoreDoc } from './modelsFromDocs';

/**
 * Defines the structure for query conditions.
 */
export interface IQueryCondition {
    field: string;
    operator: '==' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'in' | 'not-in' | 'array-contains-any';
    value: any;
}

/**
 * Defines the options for ordering query results.
 */
export interface IQueryOrder {
    field: string;
    direction: 'asc' | 'desc';
}

/**
 * Interface for search operations with text ranges
 */
export interface ISearchOptions {
    field: string;
    searchTerm: string;
    limitNum?: number;
}

/**
 * Interface for batch operations across multiple collections
 */
export interface IBatchOperation {
    type: 'create' | 'update' | 'delete';
    collectionPath: string;
    documentId?: string;
    data?: any;
    conditions?: IQueryCondition[];
}

/**
 * A generic repository for Firestore operations, providing a reusable and
 * consistent way to interact with different collections.
 */
export class FirestoreRepository<T> {
    private collectionRef: CollectionReference<DocumentData>;
    private modelClass: IModelClass<T>;

    constructor(
        private collectionPath: string,
        modelClass: IModelClass<T>
    ) {
        // The Firestore instance is now requested inside the constructor,
        // which is only called after lazy initialization in the service layer.
        this.collectionRef = collection(getFirebaseFirestore(), collectionPath);
        this.modelClass = modelClass;
    }

    private toModel(doc: DocumentData): DocumentWithId<T> {
        return modelFromDoc(this.modelClass, { docId: doc.id, data: doc.data() });
    }

    /**
     * Map a whole snapshot, letting ONE malformed document be skipped instead
     * of taking the collection down with it. The conversion (and the reasons
     * it skips rather than throws) is `modelsFromDocs` — the same function a
     * server-seeded page runs on documents the server read.
     */
    private toModelsSkippingInvalid(
        docs: readonly DocumentData[],
        context: string,
    ): DocumentWithId<T>[] {
        return modelsFromDocs(
            this.modelClass,
            docs.map((doc) => ({ docId: doc.id, data: doc.data() })),
            { context, collectionPath: this.collectionPath },
        );
    }

    /**
     * Utility method to get server timestamp for consistent usage
     */
    static getServerTimestamp() {
        return serverTimestamp();
    }

    /**
     * Utility method for array union operations
     */
    static arrayUnion(...elements: any[]) {
        return arrayUnion(...elements);
    }

    /**
     * Utility method for array remove operations
     */
    static arrayRemove(...elements: any[]) {
        return arrayRemove(...elements);
    }

    /**
     * Utility method to delete a field from a document
     */
    static deleteField() {
        return firestoreDeleteField();
    }

    async getById(id: string): Promise<DocumentWithId<T> | null> {
        // App Check is ENFORCED on Firestore in production, and a read that
        // arrives without a token is REFUSED — silently. The SDK does not throw;
        // it falls back to the local cache, finds nothing, and reports the
        // document as absent. So a read that races the token is
        // indistinguishable from "no such document", which is how a healthy
        // account rendered "we couldn't load your profile" on Safari, where the
        // reCAPTCHA challenge is slow enough to lose that race every time.
        //
        // Bounded, never rejects — see whenAppCheckReady. Once the token is in
        // hand this resolves immediately, so it costs nothing after first load.
        await whenAppCheckReady();
        logger.debug('Getting document by ID', { id, collectionPath: this.collectionPath });
        const docRef = doc(this.collectionRef, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            return null;
        }

        logger.debug('Found document with ID', { id });
        return this.toModel(docSnap);
    }

    async find(conditions: IQueryCondition[] = [], orderByArr: IQueryOrder[] = [], limitNum?: number): Promise<DocumentWithId<T>[]> {
        // Same enforced-App-Check race as getById — a query denied for a missing
        // token comes back EMPTY rather than failing, which reads as "no
        // results" to every caller.
        await whenAppCheckReady();
        logger.debug('Finding documents in collection', { collectionPath: this.collectionPath, conditionCount: conditions.length });
        const constraints: QueryConstraint[] = [];
        conditions.forEach(c => constraints.push(where(c.field, c.operator, c.value)));
        orderByArr.forEach(o => constraints.push(orderBy(o.field, o.direction)));
        if (limitNum) constraints.push(limit(limitNum));

        const q = query(this.collectionRef, ...constraints);
        const querySnapshot = await getDocs(q);

        logger.debug('Query completed', { collectionPath: this.collectionPath, documentCount: querySnapshot.docs.length });
        return this.toModelsSkippingInvalid(querySnapshot.docs, 'find');
    }

    /**
     * Server-side aggregate count matching `conditions`. Uses Firestore's
     * `getCountFromServer`, which bills one aggregate read per ~1000 index
     * entries and downloads NO documents — the correct primitive for count
     * tiles / badges that today full-read a collection just to take `.length`.
     */
    async count(conditions: IQueryCondition[] = []): Promise<number> {
        const constraints: QueryConstraint[] = [];
        conditions.forEach(c => constraints.push(where(c.field, c.operator, c.value)));
        const q = query(this.collectionRef, ...constraints);
        const snapshot = await getCountFromServer(q);
        return snapshot.data().count;
    }

    /**
     * Cursor-paginated query (CC-4). Orders by `orderByArr`, fetches `pageSize`
     * docs, and returns a `nextCursor` (the last doc id) when more exist. Pass
     * the previous `nextCursor` as `afterDocId` to fetch the next page — the
     * cursor anchors on the actual document snapshot, so the ordering fields
     * must be stable (e.g. createdAt) across calls.
     */
    async findPage(
        conditions: IQueryCondition[],
        orderByArr: IQueryOrder[],
        pageSize: number,
        afterDocId?: string | null,
    ): Promise<{ items: DocumentWithId<T>[]; nextCursor: string | null; hasMore: boolean }> {
        const constraints: QueryConstraint[] = [];
        conditions.forEach(c => constraints.push(where(c.field, c.operator, c.value)));
        orderByArr.forEach(o => constraints.push(orderBy(o.field, o.direction)));

        if (afterDocId) {
            const afterSnap = await getDoc(doc(this.collectionRef, afterDocId));
            if (afterSnap.exists()) constraints.push(startAfter(afterSnap));
        }
        // Over-fetch by one to detect whether another page exists.
        constraints.push(limit(pageSize + 1));

        const q = query(this.collectionRef, ...constraints);
        const querySnapshot = await getDocs(q);
        const hasMore = querySnapshot.docs.length > pageSize;
        const pageDocs = hasMore ? querySnapshot.docs.slice(0, pageSize) : querySnapshot.docs;
        const items = this.toModelsSkippingInvalid(pageDocs, 'findPaged');
        const nextCursor = hasMore && pageDocs.length > 0 ? pageDocs[pageDocs.length - 1].id : null;

        return { items, nextCursor, hasMore };
    }

    /**
     * Search documents by text range (for searchable fields like displayName, email)
     */
    async search(options: ISearchOptions): Promise<DocumentWithId<T>[]> {
        logger.debug('Searching documents in collection', { collectionPath: this.collectionPath, searchTerm: options.searchTerm });

        if (!options.searchTerm) return [];

        const constraints: QueryConstraint[] = [
            orderBy(options.field),
            startAt(options.searchTerm),
            endAt(options.searchTerm + '\\uf8ff')
        ];

        if (options.limitNum) {
            constraints.push(limit(options.limitNum));
        }

        const q = query(this.collectionRef, ...constraints);
        const querySnapshot = await getDocs(q);

        logger.debug('Search completed', { collectionPath: this.collectionPath, documentCount: querySnapshot.docs.length });
        return this.toModelsSkippingInvalid(querySnapshot.docs, 'find');
    }

    /**
     * Batch create multiple documents using Firestore writeBatch()
     * Automatically handles the 500 operation limit by splitting into multiple batches
     * Returns array of created documents with their IDs
     */
    async batchCreate(dataArray: PartialFirestoreWritable<T>[]): Promise<DocumentWithId<T>[]> {
        logger.debug('Batch creating documents', { collectionPath: this.collectionPath, documentCount: dataArray.length });

        const BATCH_SIZE = 500 // Firestore limit
        const results: DocumentWithId<T>[] = []

        // Split into chunks of 500
        for (let i = 0; i < dataArray.length; i += BATCH_SIZE) {
            const chunk = dataArray.slice(i, i + BATCH_SIZE)
            const batch = writeBatch(getFirebaseFirestore())
            const chunkResults: DocumentWithId<T>[] = []

            for (const data of chunk) {
                const docRef = doc(this.collectionRef)
                batch.set(docRef, {
                    ...data,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                })

                // Store doc ref to return later
                chunkResults.push({
                    ...data,
                    docId: docRef.id
                } as DocumentWithId<T>)
            }

            await batch.commit()
            logger.debug('Committed batch of documents', { chunkSize: chunk.length })
            results.push(...chunkResults)
        }

        logger.info('Batch create completed', { collectionPath: this.collectionPath, totalDocuments: results.length })
        return results
    }

    /**
     * Get all documents from a subcollection
     */
    async getSubcollectionDocuments(parentDocId: string, subcollectionName: string): Promise<DocumentWithId<any>[]> {
        logger.debug('Getting subcollection documents', { parentDocId, subcollectionName });

        const parentDocRef = doc(this.collectionRef, parentDocId);
        const subcollectionRef = collection(parentDocRef, subcollectionName);
        const querySnapshot = await getDocs(subcollectionRef);

        return querySnapshot.docs.map(doc => ({
            docId: doc.id,
            ...doc.data()
        }));
    }

    /**
     * Add document to a subcollection
     */
    async addToSubcollection(parentDocId: string, subcollectionName: string, data: any): Promise<string> {
        logger.debug('Adding to subcollection', { parentDocId, subcollectionName });

        const parentDocRef = doc(this.collectionRef, parentDocId);
        const subcollectionRef = collection(parentDocRef, subcollectionName);
        const docRef = await addDoc(subcollectionRef, {
            ...data,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        return docRef.id;
    }

    async create(data: FirestoreWritable<T> | Record<string, any>): Promise<T & { docId: string }> {
        logger.debug('Creating new document', { collectionPath: this.collectionPath });

        // Check for undefined values in the data
        const undefinedKeys = Object.keys(data as any).filter(key => (data as any)[key] === undefined);
        if (undefinedKeys.length > 0) {
            logger.error('Found undefined values in data', { undefinedKeys, data });
        }

        // Data should be a plain object. The service layer is responsible for serialization.
        const docRef = await addDoc(this.collectionRef, data as DocumentData);
        return { ...data as any, docId: docRef.id };
    }

    async createWithId(id: string, data: FirestoreWritable<T> | Record<string, any>): Promise<void> {
        logger.debug('Creating document with specified ID', { id, collectionPath: this.collectionPath });
        const docRef = doc(this.collectionRef, id);
        // Data should be a plain object. The service layer is responsible for serialization.
        await setDoc(docRef, data as DocumentData);
        logger.debug('Document created successfully', { id });
    }

    subscribeToDocument(
        id: string,
        callback: (model: DocumentWithId<T> | null) => void,
        options?: {
            /**
             * Deliver only SERVER-CONFIRMED snapshots. By default Firestore fires
             * a latency-compensated snapshot for the client's own writes before
             * the server commits (`metadata.hasPendingWrites`); a consumer that
             * triggers a SERVER call off the subscription (e.g. the invite
             * accept page auto-accepting once the profile doc "exists") would
             * race a backend that cannot see the uncommitted write yet. With
             * this flag the subscription listens with includeMetadataChanges
             * (the commit ack arrives as a metadata-only event) and suppresses
             * pending-write snapshots.
             */
            serverConfirmedOnly?: boolean;
            /**
             * Called when the LISTENER ITSELF fails (permission-denied, an
             * expired App Check token, a transport error). Without it a failed
             * listener dies silently and permanently — see below.
             */
            onError?: (error: Error) => void;
        },
    ): () => void {
        logger.debug('Subscribing to document', { id, collectionPath: this.collectionPath });
        const docRef = doc(this.collectionRef, id);
        const serverConfirmedOnly = options?.serverConfirmedOnly === true;

        const unsubscribe = onSnapshot(
            docRef,
            { includeMetadataChanges: serverConfirmedOnly },
            (docSnap) => {
                if (serverConfirmedOnly && docSnap.metadata.hasPendingWrites) {
                    logger.debug('Skipping pending-write snapshot (server-confirmed-only)', { id });
                    return;
                }
                if (docSnap.exists()) {
                    logger.debug('Snapshot received for document', { id });
                    callback(this.toModel(docSnap));
                } else {
                    logger.debug('Snapshot received but document does not exist', { id });
                    callback(null);
                }
            },
            // THE FOURTH ARGUMENT IS LOAD-BEARING. `onSnapshot` takes an error
            // observer, and omitting it means a listener that fails is torn down
            // by the SDK and never retried — silently, with nothing logged.
            //
            // That is not a hypothetical: this listener is the ONLY thing that
            // heals the `null` the profile query caches when it runs during the
            // sign-up race (the doc does not exist yet, and the global staleTime
            // is 5 minutes). If the listener dies — the App Check cold-start
            // `permission-denied` is the documented way it happens here — the
            // cached null is served for the full window and the user sits on a
            // "we couldn't load your profile" screen while their profile exists.
            // Observed in production 2026-08-08.
            //
            // `subscribeToCollection` below has always taken an `onError`; the
            // document variant simply never did.
            (error) => {
                logger.error('Document subscription failed', error, {
                    id,
                    collectionPath: this.collectionPath,
                });
                options?.onError?.(error);
            },
        );

        return unsubscribe;
    }

    /**
     * Subscribe to collection changes with real-time updates
     * Returns unsubscribe function to clean up the listener
     * 
     * @param conditions - Optional query conditions to filter results
     * @param orderByArr - Optional ordering
     * @param callback - Callback function that receives the updated models array
     * @param onError - Optional error handler
     * @returns Unsubscribe function
     */
    subscribeToCollection(
        conditions: IQueryCondition[] = [],
        orderByArr: IQueryOrder[] = [],
        callback: (models: DocumentWithId<T>[]) => void,
        onError?: (error: Error) => void,
        limitNum?: number
    ): () => void {
        logger.debug('Subscribing to collection', {
            collectionPath: this.collectionPath,
            conditionCount: conditions.length
        });

        const constraints: QueryConstraint[] = [];
        conditions.forEach(c => constraints.push(where(c.field, c.operator, c.value)));
        orderByArr.forEach(o => constraints.push(orderBy(o.field, o.direction)));
        // Bound the live watch set (mirrors find()/findPage()). Without this a
        // growing collection (e.g. orders) is streamed and re-materialized in
        // full on every write — the delivery board previously worked around the
        // gap with a post-download .slice().
        if (limitNum) constraints.push(limit(limitNum));

        const q = query(this.collectionRef, ...constraints);

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                logger.debug('Collection snapshot received', {
                    collectionPath: this.collectionPath,
                    size: snapshot.size,
                    changes: snapshot.docChanges().length,
                });

                // Convert all documents to models using the proper fromJSON method
                const models = this.toModelsSkippingInvalid(snapshot.docs, 'subscribe');
                callback(models);
            },
            (error) => {
                logger.error('Collection subscription error', {
                    collectionPath: this.collectionPath,
                    error: error.message,
                });
                if (onError) {
                    onError(error);
                }
            }
        );

        return unsubscribe;
    }

    async updateById(id: string, data: PartialFirestoreWritable<T> | Record<string, any>): Promise<void> {
        logger.debug('Updating document by ID', { id, collectionPath: this.collectionPath });
        const docRef = doc(this.collectionRef, id);
        await updateDoc(docRef, data as any);
        logger.debug('Document updated successfully', { id });
    }

    async deleteById(id: string): Promise<void> {
        logger.debug('Deleting document by ID', { id, collectionPath: this.collectionPath });
        const docRef = doc(this.collectionRef, id);
        await deleteDoc(docRef);
        logger.debug('Document deleted successfully', { id });
    }

    async updateByQuery(conditions: IQueryCondition[], data: PartialFirestoreWritable<T> | Record<string, any>): Promise<void> {
        logger.debug('Updating documents by query', { collectionPath: this.collectionPath, conditionCount: conditions.length });
        const q = query(this.collectionRef, ...conditions.map(c => where(c.field, c.operator, c.value)));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            logger.warn('Update called but no documents found for conditions', { collectionPath: this.collectionPath });
            return;
        }

        logger.debug('Found documents to update', { collectionPath: this.collectionPath, documentCount: snapshot.docs.length });
        const batch = writeBatch(getFirebaseFirestore());
        snapshot.docs.forEach(doc => {
            batch.update(doc.ref, { ...data as any, updatedAt: serverTimestamp() });
        });
        await batch.commit();
        logger.info('Documents updated successfully', { collectionPath: this.collectionPath, documentCount: snapshot.docs.length });
    }

    async deleteByQuery(conditions: IQueryCondition[]): Promise<void> {
        logger.debug('Deleting documents by query', { collectionPath: this.collectionPath, conditionCount: conditions.length });
        const q = query(this.collectionRef, ...conditions.map(c => where(c.field, c.operator, c.value)));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            logger.warn('Delete called but no documents found for conditions', { collectionPath: this.collectionPath });
            return;
        }

        logger.debug('Found documents to delete', { collectionPath: this.collectionPath, documentCount: snapshot.docs.length });
        const batch = writeBatch(getFirebaseFirestore());
        snapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
        });
        await batch.commit();
        logger.info('Documents deleted successfully', { collectionPath: this.collectionPath, documentCount: snapshot.docs.length });
    }

    /**
     * Execute a batch of operations across multiple collections
     * Static method to handle cross-collection operations
     */
    static async executeBatch(operations: IBatchOperation[]): Promise<void> {
        logger.debug('Executing batch operations', { operationCount: operations.length });
        const batch = writeBatch(getFirebaseFirestore());

        for (const operation of operations) {
            const collectionRef = collection(getFirebaseFirestore(), operation.collectionPath);

            switch (operation.type) {
                case 'create':
                    if (operation.documentId) {
                        const docRef = doc(collectionRef, operation.documentId);
                        batch.set(docRef, {
                            ...operation.data,
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp()
                        }, { merge: true });
                    } else {
                        const docRef = doc(collectionRef);
                        batch.set(docRef, {
                            ...operation.data,
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp()
                        }, { merge: true });
                    }
                    break;

                case 'update':
                    if (operation.documentId) {
                        const docRef = doc(collectionRef, operation.documentId);
                        batch.update(docRef, {
                            ...operation.data,
                            updatedAt: serverTimestamp()
                        });
                    }
                    break;

                case 'delete':
                    if (operation.documentId) {
                        const docRef = doc(collectionRef, operation.documentId);
                        batch.delete(docRef);
                    }
                    break;
            }
        }

        await batch.commit();
        logger.info('Batch operations executed successfully', { operationCount: operations.length });
    }

    /**
     * Batch create multiple documents in multiple subcollections
     */
    static async batchCreateInSubcollections(
        parentCollectionPath: string,
        parentDocId: string,
        subcollectionsData: Record<string, any[]>
    ): Promise<void> {
        logger.debug('Batch creating in subcollections', { parentCollectionPath, parentDocId });

        const batch = writeBatch(getFirebaseFirestore());

        for (const [subcollectionName, items] of Object.entries(subcollectionsData)) {
            if (Array.isArray(items)) {
                for (const item of items) {
                    const subcollectionPath = `${parentCollectionPath}/${parentDocId}/${subcollectionName}`;
                    const subcollectionRef = collection(getFirebaseFirestore(), subcollectionPath);
                    const docRef = doc(subcollectionRef);

                    batch.set(docRef, {
                        ...item,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    }, { merge: true });
                }
            }
        }

        await batch.commit();
        logger.info('Subcollection batch create completed successfully');
    }

    /**
     * Find documents across all collections with the given name (collection group query)
     * Useful for cross-tenant/cross-restaurant searches when you don't know the parent path
     * @param collectionName The name of the collection to search (e.g., 'carts', 'orders')
     * @param modelClass The model class to use for data conversion
     * @param conditions Query conditions to apply
     * @param orderByArr Query ordering
     * @param limitNum Maximum number of results
     * @returns Promise resolving to array of documents with their data
     */
    static async findInCollectionGroup<T>(
        collectionName: string,
        modelClass: IModelClass<T>,
        conditions: IQueryCondition[] = [],
        orderByArr: IQueryOrder[] = [],
        limitNum?: number
    ): Promise<DocumentWithId<T>[]> {
        logger.debug('Collection group query', { collectionName, conditionCount: conditions.length });

        const constraints: QueryConstraint[] = [];
        conditions.forEach(c => constraints.push(where(c.field, c.operator, c.value)));
        orderByArr.forEach(o => constraints.push(orderBy(o.field, o.direction)));
        if (limitNum) constraints.push(limit(limitNum));

        const collectionGroupRef = collectionGroup(getFirebaseFirestore(), collectionName); // scoped-path-ok: not location-scoped (generic primitive; the caller names the group)
        const q = query(collectionGroupRef, ...constraints);
        const querySnapshot = await getDocs(q);

        logger.debug('Collection group query completed', { collectionName, documentCount: querySnapshot.docs.length });

        return querySnapshot.docs.map(doc => {
            const model = modelClass.fromJSON(doc.data());
            (model as DocumentWithId<T>).docId = doc.id;
            return model as DocumentWithId<T>;
        });
    }
}
